import React, { useRef, useEffect } from 'react';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import SvgIcon from '../../../../utils/SvgIcon';
import { getCurrentWeekRange } from '../../../../utils/DateHelpers';
import { useJobsConfig } from '../../../../PortalJobListing/hooks/useJobsConfig';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

type NextJobSummaryFivePlusServicesProps = {
  weekSummary?: {
    scheduledTotal?: number;
    completedTotal?: number;
    rejectedTotal?: number;
    url?: string;
  };
  monthlySummary?: {
    scheduledTotal?: number;
    scheduledUrl?: string;
    completedTotal?: number;
    completedUrl?: string;
  };
};

const NextJobSummaryFivePlusServices = ({ weekSummary, monthlySummary }: NextJobSummaryFivePlusServicesProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart<'doughnut'> | null>(null);

  const { labels } = useJobsConfig();

  const scheduledTotal = weekSummary?.scheduledTotal ?? 0;
  const completedTotal = weekSummary?.completedTotal ?? 0;
  const rejectedTotal = weekSummary?.rejectedTotal ?? 0;
  const totalJobs = scheduledTotal + completedTotal + rejectedTotal;

  const currentWeek = getCurrentWeekRange();

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstanceRef.current?.destroy();

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const scheduledLabel = labels.scheduled;
    const completedLabel = labels.completed;
    const rejectedLabel = labels.rejected;

    chartInstanceRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [scheduledLabel, completedLabel, rejectedLabel],
        datasets: [
          {
            data: [scheduledTotal, completedTotal, rejectedTotal],
            backgroundColor: ['#E2E2E2', '#38B15F', '#E30606'],
            borderWidth: 0,
            borderRadius: 4,
            spacing: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
    });

    return () => {
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
    };
  }, [scheduledTotal, completedTotal, rejectedTotal, labels.scheduled, labels.completed, labels.rejected]);

  return (
    <div className="next-job-summary">
      <div className="next-job-summary__body">
        <div className="next-job-summary__header">
          <div className="next-job-summary__header-content">
            <div className="next-job-summary__title-wrapper">
              <div className="next-job-summary__title">{labels.thisWeek}</div>
              <div className="next-job-summary__title-date">{currentWeek}</div>
            </div>
          </div>
        </div>

        <div className="next-job-summary__chart-container">
          <div className="next-job-summary__chart-wrapper">
            <canvas ref={chartRef} className="next-job-summary__chart" />
            <div className="next-job-summary__chart-center">
              <div className="next-job-summary__chart-center-number">{totalJobs}</div>
              <div className="next-job-summary__chart-center-label">{labels.jobs}</div>
            </div>
          </div>

          <div className="next-job-summary__legend">
            <div className="next-job-summary__legend-item">
              <div className="next-job-summary__status-card-indicator next-job-summary__status-card-indicator--scheduled" />
              <span className="next-job-summary__legend-label">{labels.scheduled}</span>
              <span className="next-job-summary__legend-count">{scheduledTotal}</span>
            </div>

            <div className="next-job-summary__legend-item">
              <div className="next-job-summary__status-card-indicator next-job-summary__status-card-indicator--completed" />
              <span className="next-job-summary__legend-label">{labels.completed}</span>
              <span className="next-job-summary__legend-count">{completedTotal}</span>
            </div>

            <div className="next-job-summary__legend-item">
              <div className="next-job-summary__status-card-indicator next-job-summary__status-card-indicator--rejected" />
              <span className="next-job-summary__legend-label">{labels.rejected}</span>
              <span className="next-job-summary__legend-count">{rejectedTotal}</span>
            </div>

            {weekSummary?.url && (
              <div className="next-job-summary__schedule-link">
                <a href={weekSummary.url} className="next-job-summary__schedule-link-text">
                  {labels.viewSchedule}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="next-job-summary__footer-container">
        {monthlySummary?.scheduledUrl && (
          <a href={monthlySummary.scheduledUrl} className="next-job-summary__footer">
            <div className="next-job-summary__footer-text">
              <span className="next-job-summary__footer-text-number">{monthlySummary.scheduledTotal ?? 0}</span>
              <span className="next-job-summary__footer-text-label">{labels.moreJobsMonth}</span>
            </div>
            <div className="next-job-summary__footer-icon">
              <SvgIcon
                viewBox="0 0 16 16"
                iconName="arrow-right-portal"
                className="next-job-summary__footer-icon-icon"
              />
            </div>
          </a>
        )}

        {monthlySummary?.completedUrl && (
          <a href={monthlySummary.completedUrl} className="next-job-summary__footer">
            <div className="next-job-summary__footer-text">
              <span className="next-job-summary__footer-text-number">{monthlySummary.completedTotal ?? 0}</span>
              <span className="next-job-summary__footer-text-label">{labels.jobsCompletedMonth}</span>
            </div>
            <div className="next-job-summary__footer-icon">
              <SvgIcon
                viewBox="0 0 16 16"
                iconName="arrow-right-portal"
                className="next-job-summary__footer-icon-icon"
              />
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

export default NextJobSummaryFivePlusServices;
